import { createContext, useCallback, useContext, useEffect, useMemo, type ReactNode } from "react";
import { toast } from "sonner";
import {
    appendTranscriptLine,
    applyCallDraft,
    clearCallSession,
    openTelephonyCall,
    openWalkupSession,
    resolveMissedCall,
    type CallAiDraft,
    type IncomingCall,
    type TranscriptLine,
} from "@/lib/use-incoming-call";
import { useSseEvent } from "@/lib/use-sse";
import { useAuth } from "@/lib/auth";

interface CallPanelContextValue {
    /** Open a manual walk-up session — the panel starts in search mode. */
    open: () => void;
    /** Open a telephony session (real/mock inbound call) — starts ringing. */
    openWithCall: (call: IncomingCall) => void;
    /** Dismiss the panel. */
    close: () => void;
}

const CallPanelContext = createContext<CallPanelContextValue | null>(null);

/**
 * Global open/close handle for the Call Panel (the evolved CallDock). Mounted
 * high in the app shell so the top bar, a hotkey, or a future telephony webhook
 * can drive the panel from anywhere without navigating. The panel itself reads
 * the active session straight off the CallBus via useIncomingCall.
 */
export function CallPanelProvider({ children }: { children: ReactNode }) {
    // Gate the call SSE listeners on the SAME audience that mounts the Call
    // Panel + softphone in the app shell: everyone who isn't tech-only. Using a
    // narrower `isOffice` here (office/front-desk/manager/admin only) meant a
    // non-office role that still has the softphone — e.g. a Field Supervisor —
    // would hear the softphone ring but never get the screen-pop, since the
    // listener never attached. Keep this in lockstep with app-shell's `techOnly`.
    const { isField, isOffice, isAdmin } = useAuth();
    const canHandleCalls = !(isField && !isOffice && !isAdmin);
    const open = useCallback(() => openWalkupSession(), []);
    const openWithCall = useCallback((call: IncomingCall) => openTelephonyCall(call), []);
    const close = useCallback(() => clearCallSession(), []);

    // Screen pop: a tracked inbound call logged server-side broadcasts a
    // `call:inbound` SSE event scoped to the org. Office clients pop the panel
    // pre-loaded with the matched customer (or in search mode when unmatched).
    const handleInbound = useCallback(
        (e: MessageEvent) => {
            try {
                const d = JSON.parse(e.data) as {
                    fromNumber?: string;
                    matchedCustomerId?: string | null;
                    matchedCustomerName?: string | null;
                    sourceTag?: string | null;
                    callSid?: string | null;
                };
                openWithCall({
                    id: typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : `call-${Date.now()}`,
                    fromNumber: d.fromNumber ?? "",
                    fromName: d.matchedCustomerName ?? undefined,
                    matchedCustomerId: d.matchedCustomerId ?? undefined,
                    callSid: d.callSid ?? undefined,
                    receivedAt: Date.now(),
                    // A real inbound call must open in "ringing" so the panel
                    // shows the Answer button (RingingActions) wired to the
                    // softphone — for matched AND unmatched callers alike. It
                    // used to jump matched callers straight to "active" ("On
                    // Call"), which skipped Answer entirely, so the softphone
                    // kept ringing with no way to pick up. Answering transitions
                    // to "active". The matched customer still shows while ringing.
                    status: "ringing",
                    origin: "telephony",
                });
            } catch {
                // ignore malformed payloads
            }
        },
        [openWithCall],
    );
    useSseEvent("call:inbound", handleInbound, canHandleCalls);

    // AI copilot: the media bridge pushes an updated job draft as the call is
    // transcribed. Merge it into the active session so the panel can show it.
    const handleDraft = useCallback((e: MessageEvent) => {
        try {
            const d = JSON.parse(e.data) as { draft?: CallAiDraft };
            if (d.draft) applyCallDraft(d.draft);
        } catch {
            // ignore malformed payloads
        }
    }, []);
    useSseEvent("call:draft", handleDraft, canHandleCalls);

    // Live subtitles: the media bridge pushes each finalized caller utterance as
    // its own line. Append it to the active session so the panel scrolls the
    // conversation as it happens.
    const handleTranscript = useCallback((e: MessageEvent) => {
        try {
            const d = JSON.parse(e.data) as { callSid?: string | null } & Partial<TranscriptLine>;
            if (typeof d.text !== "string" || !d.text || typeof d.seq !== "number") return;
            appendTranscriptLine(
                {
                    seq: d.seq,
                    speaker: d.speaker === "office" ? "office" : "caller",
                    text: d.text,
                    ts: typeof d.ts === "number" ? d.ts : Date.now(),
                },
                d.callSid ?? undefined,
            );
        } catch {
            // ignore malformed payloads
        }
    }, []);
    useSseEvent("call:transcript", handleTranscript, canHandleCalls);

    // Missed call: the dial-status webhook reports nobody answered. Toast the
    // office so a missed call is never silent, and close the panel if it's still
    // showing that (never-answered) caller.
    const handleMissed = useCallback((e: MessageEvent) => {
        try {
            const d = JSON.parse(e.data) as {
                fromNumber?: string;
                matchedCustomerName?: string | null;
            };
            const who = d.matchedCustomerName || d.fromNumber || "Unknown caller";
            toast.warning("Missed call", { description: `${who} — no one answered.` });
            if (d.fromNumber) resolveMissedCall(d.fromNumber);
        } catch {
            // ignore malformed payloads
        }
    }, []);
    useSseEvent("call:missed", handleMissed, canHandleCalls);

    // Voicemail: a missed caller left a message (stored + attached to the call
    // log). Toast the office so it's noticed and can be played back from Call Logs.
    const handleVoicemail = useCallback((e: MessageEvent) => {
        try {
            const d = JSON.parse(e.data) as {
                fromNumber?: string;
                matchedCustomerName?: string | null;
                durationSeconds?: number | null;
            };
            const who = d.matchedCustomerName || d.fromNumber || "Unknown caller";
            const secs = d.durationSeconds ? ` (${d.durationSeconds}s)` : "";
            toast.info("New voicemail", { description: `${who}${secs} — play it back in Call Logs.` });
        } catch {
            // ignore malformed payloads
        }
    }, []);
    useSseEvent("call:voicemail", handleVoicemail, canHandleCalls);

    // Global hotkey: Cmd/Ctrl+J opens a manual walk-up session. Cmd+K stays the
    // command palette; J is unclaimed. Same listener pattern as the palette.
    useEffect(() => {
        const down = (e: KeyboardEvent) => {
            if (e.key.toLowerCase() === "j" && (e.metaKey || e.ctrlKey)) {
                e.preventDefault();
                open();
            }
        };
        document.addEventListener("keydown", down);
        return () => document.removeEventListener("keydown", down);
    }, [open]);

    const value = useMemo(() => ({ open, openWithCall, close }), [open, openWithCall, close]);
    return <CallPanelContext.Provider value={value}>{children}</CallPanelContext.Provider>;
}

export function useCallPanel(): CallPanelContextValue {
    const ctx = useContext(CallPanelContext);
    if (!ctx) {
        throw new Error("useCallPanel must be used within a CallPanelProvider");
    }
    return ctx;
}
